import { RowNode } from 'ag-grid-community';
import { AgGridColumn } from 'ag-grid-react/lib/agGridColumn';
import React, { useEffect, useState } from 'react';
import { EnvelopeFill, Eye, FileText, PersonFill } from 'react-bootstrap-icons';
import { connect, useSelector } from 'react-redux';
import { ConnectedHcpGridWithGQL } from '.';
import { ServiceRequestViewFragment, useGetSrHubStatusesQuery, useServiceRequestInfoQuery } from '../graphql';
import { AnyType } from '../interfaces';
import { AppRootState, tenancyFeatureSelector } from '../redux';
import {
  ActionColumnActions,
  actionsColumnDefinition,
  dateColumnDefinition,
  featureGridColumnsSelector,
  getFeatureGridColumnProps,
  idColumnDefinition,
  nextStepColumnWithoutTooltip,
  prescriberColumnDefinition,
  nameColumnDefinition,
  ROUTE_PATHS,
  serviceRequestAlertColumnDefinition,
  filterGridComparator,
  dobDateColumnFormat,
  getConfigurationDataKey,
  generalColumnDefinition,
  requestStatusColumnDefinition
} from '../utils';
import { GridFilters } from './GridFilters';

interface RequestsTableProps {
  enableExternalFilters?: boolean;
  filteredStatus?: string;
}

export interface StatusDescription {
  statusDescription: string;
  __typename: string;
}
export interface FilterInterface {
  primaryFilter?: string;
  secondaryFilterList?: Set<string>;
  strictCompare?: boolean;
}

const columnSelector = featureGridColumnsSelector(getConfigurationDataKey('service-request.table'));

export const RequestsTable = (props: RequestsTableProps) => {
  const programId = Number(useSelector((state: AppRootState) => state.auth.session?.user?.ProgramId));
  const drugList: AnyType = useSelector((state: AppRootState) => state?.app?.entities?.drugList);

  const [gridFilter, setGridFilter] = useState<FilterInterface>({ strictCompare: true });
  const [filter, setFilter] = useState<string>();
  const [filterList, setFilterList] = useState<Set<string>>(new Set([]));
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [statusDescriptions, setStatusDescriptions] = useState<StatusDescription[]>([]);
  const requestsFilterMapping: AnyType = useSelector<AnyType>(tenancyFeatureSelector('sr.filters_v1.5'));

  useEffect(() => {
    setGridFilter({ ...gridFilter, secondaryFilterList: filterList, primaryFilter: filter });
  }, [filterList, filter]);

  const filterProps = props.enableExternalFilters
    ? {
        isFilterActive: true,
        filterComparator: function (node: RowNode, filter?: FilterInterface) {
          return filterGridComparator(node, filter, requestsFilterMapping);
        },
        filterValue: gridFilter
      }
    : {};

  const columns = useSelector(columnSelector);

  // if there is no columns or druglist comes from state then return null else show the grid
  if (!columns || !drugList) return null;

  const isDrugs = drugList?.[0] ? 'false' : 'true';
  const statusOptions = [
    { statusDescription: 'Cancelled' },
    { statusDescription: 'Insurance Not found' },
    { statusDescription: 'In progress' }
    // Add more options as needed
  ];
  return (
    <>
      {props.enableExternalFilters && (
        <GridFilters
          //filterName='Requests'
          onFilterChange={(filter: string) => setFilter(filter)}
          hideDropDownFilter={columns?.patientHubStatus?.hide}
          isPatientsView={false}
        />
      )}

      <div className='site-background'>
        <ConnectedHcpGridWithGQL
          className='grid-wrapper request-grid'
          descriptionType='SrHubStatusDescription'
          {...filterProps}
          queryHook={useServiceRequestInfoQuery}
          onDataLoad={(statusDescriptions: AnyType[]) => {
            setStatusDescriptions(statusDescriptions);
          }}
          queryOptions={{
            variables: {
              programId
            }
          }}
          pagination>
          <AgGridColumn
            field='patientLastName'
            tooltipField='patientLastName'
            resizable={true}
            {...generalColumnDefinition()}
            {...nameColumnDefinition(columns.patientLastName)}
          />
          <AgGridColumn
            field='patientFirstName'
            tooltipField='patientFirstName'
            resizable={true}
            {...generalColumnDefinition()}
            {...nameColumnDefinition(columns.patientFirstName)}
          />
          <AgGridColumn
            {...dobDateColumnFormat('patientDOB')}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.dob)}
          />
          <AgGridColumn
            {...idColumnDefinition('patientId')}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.patientId)}
          />
          <AgGridColumn
            {...prescriberColumnDefinition()}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.prescriber)}
          />
          <AgGridColumn
            {...dateColumnDefinition('requestStartDate')}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.submitted)}
          />
          <AgGridColumn
            field='requestType'
            tooltipField='requestType'
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.requestType)}
          />
          <AgGridColumn
            field='sRDrugName'
            tooltipField='Drug Name'
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.drugName)}
          />
          <AgGridColumn
            {...idColumnDefinition('id')}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.id)}
            hide='true'
          />
          <AgGridColumn
            field='requestID'
            tooltipField='requestID'
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.id)}
          />
          <AgGridColumn
            field='statusText'
            tooltipField='statusText'
            resizable={true}
            {...generalColumnDefinition()}
            {...requestStatusColumnDefinition('patientHubStatus', columns.status, requestsFilterMapping)}
            {...getFeatureGridColumnProps(columns.status)}
          />
          <AgGridColumn
            {...nextStepColumnWithoutTooltip()}
            resizable={true}
            {...generalColumnDefinition()}
            {...getFeatureGridColumnProps(columns.nextSteps)}
          />
          <AgGridColumn
            resizable={false}
            {...actionsColumnDefinition((node: RowNode): ActionColumnActions[] => [
              {
                href: `${ROUTE_PATHS.requests}/${node.data.id}/${node.data.patientId}/${node.data.requestType}/${node.data.requestID}/${node.data.passingStatusValue}`,
                label: 'View Request',
                icon: <i className='fa-light fa-eye'></i>
              },
              {
                href: `${ROUTE_PATHS.serviceRequest}/patient/${node.data.patientId}`,
                label: 'Start New Request',
                featureKey: 'service-request.manage',
                icon: <i className='fa-light fa-file-circle-plus'></i>
              },
              {
                href: `${ROUTE_PATHS.messages}?patient=${node.data.patientId}&request=${node.data.id}#compose`,
                label: 'Send a Message',
                featureKey: 'secure-message.manage',
                icon: <i className='fa-light fa-envelope'></i>
              },
              {
                href: `${ROUTE_PATHS.patients}/${node.data.patientId}#summary`,
                label: 'Patient Profile',
                icon: <i className='fa-regular fa-user-injured'></i>
              }
            ])}
          />
        </ConnectedHcpGridWithGQL>
      </div>
    </>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {};

export const ConnectedRequestsTable = connect(mapStateToProps, mapDispatchToProps)(RequestsTable);
